import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Snackbar } from '@mui/material';
import './index.css';

import iphoneIcon from '../../assets/icons/iphone.png';
import itrocaLogo from '../../assets/icons/Logo-iTroca-preta.png';
import imeiImage from '../../assets/icons/Tela_imei.jpeg';
import CustomModal from '../customModal';

import { getModelosIphone } from '../../services/modeloService';
import { getCapacidadesByModelo } from '../../services/capacidadeService';
import { getCoresByModelo } from '../../services/corService';
import { criarAvaliacaoRequest } from '../../store/duck/avaliacao';

import useGeolocation from '../../hooks/useGeolocation';
import store from '../../store';

export default function Calculater({ perfil }) {
  const dispatch = useDispatch();
  const { uf } = useGeolocation();

  // Controle de envio (não aciona re-render)
  const isSubmittingRef = useRef(false);

  // Estados do formulário
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedCapacity, setSelectedCapacity] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [imei, setImei] = useState('');
  const [noImei, setNoImei] = useState(false);

  const [models, setModels] = useState([]);
  const [capacities, setCapacities] = useState([]);
  const [colors, setColors] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const isImeiValid = imei.trim() !== '';
  const canSubmit = isImeiValid || noImei;

  const parseCapacity = (cap) => {
    const normalized = cap.toLowerCase().replace('gb', '').replace('tb', '');
    const isTb = cap.toLowerCase().includes('tb');
    const value = parseFloat(normalized);
    return isTb ? value * 1024 : value;
  };

  // Buscar modelos
  useEffect(() => {
    getModelosIphone()
      .then(setModels)
      .catch(console.error);
  }, []);

  // Buscar capacidades
  useEffect(() => {
    if (selectedModel) {
      setSelectedCapacity('');
      setSelectedColor('');
      getCapacidadesByModelo(selectedModel)
        .then((res) => {
          const sorted = res.sort((a, b) => parseCapacity(a) - parseCapacity(b));
          setCapacities(sorted);
        })
        .catch(console.error);
    }
  }, [selectedModel]);

  // Buscar cores
  useEffect(() => {
    if (selectedModel && selectedCapacity) {
      getCoresByModelo(selectedModel)
        .then(setColors)
        .catch(console.error);
    }
  }, [selectedModel, selectedCapacity]);

  // Função que cria a avaliação e redireciona para a URL externa
  const handleAvaliar = () => {
    if (isSubmittingRef.current) {
      console.warn('[handleAvaliar] Ignorado: envio já em andamento');
      return;
    }
    isSubmittingRef.current = true;
  
    const corSelecionadaObj = colors.find((c) => c.nomeCor === selectedColor);
    const imagemUrl = corSelecionadaObj?.imagemUrl || iphoneIcon;
  
    // Converte o uf do geolocation e o uf da loja para uppercase para comparação
    const ufUsuario = uf?.toUpperCase() || '';
    const ufLoja = perfil?.uf?.toUpperCase() || '';
  
    const payload = {
      modelo: selectedModel,
      capacidade: selectedCapacity,
      cor: selectedColor,
      imei: noImei ? null : imei,
      uf: ufUsuario,
      idComercial: perfil?.idComercial,
      imagemUrl,
      leadLoja: true,
      // Se o uf do usuário for o mesmo da loja e o gatilho de cálculo personalizado estiver ativo,
      // utiliza o idComercial; caso contrário, define como 'iTroca'.
      tipoCalculo: (ufUsuario === ufLoja && perfil?.gatilhoCalculoPersonalizado) 
                    ? perfil.idComercial 
                    : 'iTroca',
    };
  
    dispatch(criarAvaliacaoRequest(payload));
  
    // Aguarda a criação do docRefAvaliacao e redireciona para a URL externa
    const interval = setInterval(() => {
      const ref = store.getState().avaliacao?.avaliacaoId?.docRefAvaliacao;
      if (ref) {
        clearInterval(interval);
        sessionStorage.setItem('docRefAvaliacao', ref);
        const finalUrl = `https://app.itroca.com.br/avaliacao?docRefAvaliacao=${ref}`;
        window.location.href = finalUrl;
        isSubmittingRef.current = false;
      }
    }, 200);
  };
  

  return (
    <div className="calculater-container">
      <img src={iphoneIcon} alt="iPhone" className="calculater-icon" />
      <h4 className="calculater-subtitle">CALCULADORA DE AVALIAÇÃO</h4>
      <h2 className="calculater-title">DESCUBRA QUANTO VALE SEU IPHONE</h2>
      <select
        className="calculater-dropdown"
        value={selectedModel}
        onChange={(e) => setSelectedModel(e.target.value)}
      >
        <option value="">SELECIONE O MODELO</option>
        {models.map((model) => (
          <option key={model} value={model}>
            {model}
          </option>
        ))}
      </select>
      {selectedModel && (
        <div className="calculater-options">
          {capacities.map((cap) => (
            <button
              key={cap}
              className={`option-button ${selectedCapacity === cap ? 'selected' : ''}`}
              onClick={() => setSelectedCapacity(cap)}
            >
              {cap}
            </button>
          ))}
        </div>
      )}
      {selectedCapacity && colors.length > 0 && (
        <>
          <div className="calculater-colors">
            {colors.map((color) => (
              <div
                key={color.nomeCor}
                className={`color-circle ${selectedColor === color.nomeCor ? 'selected' : ''}`}
                style={{ backgroundColor: color.codigoCor }}
                onClick={() => setSelectedColor(color.nomeCor)}
              />
            ))}
          </div>
          {selectedColor && <div className="color-name">{selectedColor}</div>}
        </>
      )}
      {selectedColor && (
        <>
          <input
            className="calculater-imei"
            type="text"
            placeholder="Digite o IMEI do seu iPhone"
            value={imei}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '');
              if (val.length <= 15) setImei(val);
            }}
            disabled={noImei}
          />
          <button className="link-imei" onClick={() => setShowModal(true)}>
            Como localizar o IMEI do seu iPhone
          </button>
          <label className="switch-wrapper">
            <input
              type="checkbox"
              checked={noImei}
              onChange={() => setNoImei(!noImei)}
            />
            <span className="switch-slider" />
            <span className="switch-label">
              Não localizou o IMEI? Avaliar sem IMEI
            </span>
          </label>
        </>
      )}
      {selectedColor && (
            <button
            className="avaliar-btn"
            disabled={!canSubmit || isSubmittingRef.current}
            onClick={handleAvaliar}
          >
            Avaliar
          </button>
      )}
      <div className="powered-by">
        <span>Powered By</span>
        <img src={itrocaLogo} alt="iTroca logo" />
      </div>
      <CustomModal isOpen={showModal} onClose={() => setShowModal(false)}>
        <h3>Como identificar o IMEI do seu iPhone</h3>
        <p>
          Digite <strong>*#06#</strong> no discador
          <br /> ou vá em Ajustes &gt; Geral &gt; Sobre.
        </p>
        <img src={imeiImage} alt="IMEI exemplo" className="modal-img" />
      </CustomModal>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </div>
  );
}
